import { useEffect, useState, useCallback } from "react";
import { Heart, Plus, Trash2, BookHeart, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function Wishlist() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);

  useEffect(() => {
    if (title.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/books/suggest", { params: { q: title.trim() } }); setSuggestions(data); }
      catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [title]);

  const pickSuggestion = (s) => {
    setTitle(s.title);
    if (!author && s.author) setAuthor(s.author);
    setSuggestions([]);
    setShowSug(false);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/wishlist");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post("/wishlist", { title, author });
      toast.success("Aggiunto alla lista dei desideri");
      setTitle(""); setAuthor(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/wishlist/${id}`); load(); } catch {}
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-950 flex items-center justify-center"><BookHeart className="w-6 h-6" /></div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">Lista dei desideri</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6 flex items-center gap-1.5"><Bell className="w-4 h-4" />Ti avviseremo quando qualcuno pubblica un libro che stai cercando.</p>

      <form onSubmit={add} className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4 mb-8">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="relative">
            <Label htmlFor="wt">Titolo cercato *</Label>
            <Input id="wt" data-testid="wishlist-title" value={title}
              onChange={(e) => { setTitle(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              autoComplete="off" placeholder="Es. Il piccolo principe" className="mt-1.5 h-11" required />
            {showSug && suggestions.length > 0 && (
              <div data-testid="wishlist-suggestions" className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-border bg-popover shadow-lg overflow-hidden">
                {suggestions.map((s, i) => (
                  <button key={`${s.title}-${s.author || ""}-${i}`} type="button" data-testid={`suggestion-${i}`}
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors">
                    <span className="text-sm font-medium">{s.title}</span>
                    {s.author && <span className="text-xs text-muted-foreground block">{s.author}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="wa">Autore <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
            <Input id="wa" data-testid="wishlist-author" value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1.5 h-11" />
          </div>
        </div>
        <Button type="submit" data-testid="wishlist-add" disabled={saving || !title.trim()} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" />Aggiungi</Button>
      </form>

      {items.length === 0 ? (
        <div className="text-center py-14 rounded-2xl border border-dashed border-border text-muted-foreground">
          <Heart className="w-9 h-9 mx-auto mb-3" />La tua lista è vuota.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((w) => (
            <div key={w.id} data-testid={`wishlist-item-${w.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
              <Heart className="w-5 h-5 text-rose-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{w.title}</p>
                {w.author && <p className="text-sm text-muted-foreground truncate">{w.author}</p>}
              </div>
              <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" data-testid={`wishlist-del-${w.id}`} onClick={() => remove(w.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
